import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { NzIconService } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, Subscription, switchMap, take, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MenuService } from '../../core/menu.service';
import { UserService } from '../../core/user.service';
import { AccessModules, MenuItem } from '../../shared/models/user.model';
import {
  activityIcon,
  clusterIcon,
  commentIcon,
  donateIcon,
  lotIcon,
  machineryIcon,
  materialIcon,
  packageIcon,
  paperHorizontal,
  userHardHatIcon,
  insertColumn,
  insertRow,
  trash,
  tasks,
  kanban,
  taskAll,
  DIM
} from '../../utils/icons';
import mixpanel from 'mixpanel-browser';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, OnDestroy {
  menuItems: MenuItem[] = [];
  isCollapsed = false;
  currentYear = new Date().getFullYear();
  isLoggedIn = false;
  currentPath: string;
  private destroy = new Subject<void>();
  private accessModules?: AccessModules;
  private companyName: string = '';
  private routerSubscription!: Subscription;

  constructor(
    public authService: AuthService,
    private menuService: MenuService,
    private iconService: NzIconService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
    private messageService: NzMessageService,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.iconService.addIconLiteral('diazar:user-hard-hatOutlined', userHardHatIcon);
    this.iconService.addIconLiteral('diazar:activityIcon', activityIcon);
    this.iconService.addIconLiteral('diazar:packageIcon', packageIcon);
    this.iconService.addIconLiteral('diazar:materialIcon', materialIcon);
    this.iconService.addIconLiteral('diazar:machineryIcon', machineryIcon);
    this.iconService.addIconLiteral('diazar:lotIcon', lotIcon);
    this.iconService.addIconLiteral('diazar:clusterIcon', clusterIcon);
    this.iconService.addIconLiteral('diazar:commentIcon', commentIcon);
    this.iconService.addIconLiteral('diazar:donateIcon', donateIcon);
    this.iconService.addIconLiteral('diazar:paperHorizontal', paperHorizontal);
    this.iconService.addIconLiteral('diazar:insertColumn', insertColumn);
    this.iconService.addIconLiteral('diazar:insertRow', insertRow);
    this.iconService.addIconLiteral('diazar:trash', trash);
    this.iconService.addIconLiteral('diazar:tasks', tasks);
    this.iconService.addIconLiteral('diazar:kanban', kanban);
    this.iconService.addIconLiteral('diazar:taskAll', taskAll);
    this.iconService.addIconLiteral('diazar:dim', DIM);

    this.currentPath = this.router.url;
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentPath = event.url;
      }
    });

  }

  checkRoute(target?: string) {
    if(!target) return false;
    let elements = target.split('/');
    elements = elements.filter(x => x != '');
    if(elements.length == 0) {
      elements = ['dashboard'];
    }
    
    return this.containsAnyElement(elements);
  }

  isRouteSelected(target?: string) {
    return this.checkRoute(target);
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Handset])
      .pipe(takeUntil(this.destroy))
      .subscribe(result => this.isCollapsed = result.matches);

    this.isLoggedIn = this.userService.hasTokenValid();
    this.menuService.getMenuItems()
      .pipe(switchMap((data) => {
        this.menuItems = data;
        return this.userService.getDataUser();
      }), takeUntil(this.destroy))
      .subscribe((user) => {
        this.companyName = user.company.name || '';
        this.accessModules = user.user.accessModules ?? {};
        this.validateAccessModulesPermissions(this.menuItems[1])
      });
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.routerSubscription.unsubscribe();
  }

  handleToggle() {
    this.isCollapsed = !this.isCollapsed;
  }

  get theme(): 'dark' | 'light' {
    return this.isCollapsed ? 'dark' : 'light';
  }

  // --- Sidebar Methods --- //
  isSubmenu(item: MenuItem): boolean {
    return !!item.children && item.children.length > 0;
  }

  hasIcon(item: MenuItem): string {
    return item.icon ? item.icon : '';
  }
  // --- Sidebar Methods End --- //


  onLogout() {
    localStorage.removeItem('companyName');
    this.authService.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }

  // funcion para redirigir al usuario al path que reciba como parametro, ejemplo "/requisiciones"
  redirectTo(path: string | undefined) {
    if (path) {
      if(path.includes('requisiciones')) {
        mixpanel.track('Requisitions Module Opened', {
          user_id: this.userService.userMy?.user.id,
          role: this.userService.userMy?.user.role,
          company: this.userService.userMy?.company.name,
          entry_point: 'sidebar_menu'
        });
      }
      this.router.navigate([path]);
    }
  }

  onGoToUserProfile() {
    if (this.companyName === '') {
      this.messageService.error('Debes pertenecer una empresa para poder acceder a tu perfil');
      return;
    }
    window.open(`https://${environment.usersControlUrl}/${this.companyName}`, '_blank');
  }

  private containsAnyElement(elements: string[]): boolean {
    return elements.some(element => this.currentPath.includes(element));
  }

  private validateAccessModulesPermissions(menuItem: MenuItem) {
    if (!('children' in menuItem)) return;
    for (const item of menuItem.children!) {
      switch (item.title) {
        case 'Requisiciones':
          item.hasAccess = this.accessModules?.requisitions;
          break;
        case 'Estimaciones':
          item.hasAccess = this.accessModules?.workProgressStatement;
          break;
      }
    }
  }
}
